import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ─── Slot group definitions ───────────────────────────────────────────────────

const card1Def: SlotGroupDefinition = {
  label: 'Card 1 — Make a Payment',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[6].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Make a payment',
    },
    title: {
      label: 'Card Title',
      type: 'text',
      defaultValue: 'Make a Payment',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
      defaultValue: '#',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 6,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a real property photo of the building exterior, entrance, or signage for a "Make a Payment" resident-access card.',
    context:
      'One of three resident-access cards in this section — each card must use a different photo AND a different type of shot. This card should be an exterior/architectural view (the other two use an interior space and a community/amenity or neighborhood view).',
    sourceCollection: 'photo_gallery',
  },
};

const card2Def: SlotGroupDefinition = {
  label: 'Card 2 — Resident Portal',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[7].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Resident portal',
    },
    title: {
      label: 'Card Title',
      type: 'text',
      defaultValue: 'Resident Portal',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
      defaultValue: '#',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 7,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a real property photo of a furnished interior — a unit living space, kitchen, or model home — for a "Resident Portal" resident-access card.',
    context:
      'One of three resident-access cards in this section — each card must use a different photo AND a different type of shot. This card should be an interior living space (the other two use a building exterior and a community/amenity or neighborhood view).',
    sourceCollection: 'photo_gallery',
  },
};

const card3Def: SlotGroupDefinition = {
  label: 'Card 3 — Contact Us',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[8].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Contact us',
    },
    title: {
      label: 'Card Title',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 8,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a real property photo of a shared amenity or community space — pool, clubhouse, lounge, courtyard, or a neighborhood view — for a "Contact Us" resident-access card.',
    context:
      'One of three resident-access cards in this section — each card must use a different photo AND a different type of shot. This card should be a community/amenity or neighborhood view (the other two use a building exterior and an interior living space).',
    sourceCollection: 'photo_gallery',
  },
};

// ─── Card sub-component ──────────────────────────────────────────────────────

interface CardSlot {
  image?: string;
  alt?: string;
  title?: string;
  url?: string;
  _target?: string;
}

function ResourceCard({ slot, fallbackUrl }: { slot: CardSlot; fallbackUrl: string }) {
  const url = slot.url || fallbackUrl;
  const target = slot._target || (url.startsWith('http') || url.startsWith('//') ? '_blank' : '_self');

  return (
    <a
      href={url}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      className="relative block rounded-xl overflow-hidden group w-full"
      style={{ aspectRatio: '1/1' }}
      aria-label={slot.title || 'Open'}
    >
      {slot.image ? (
        <VibesitesImage
          url={slot.image}
          alt={slot.alt ?? slot.title ?? ''}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
      )}

      {/* Dark gradient overlay for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />

      {/* Title (bottom-left) */}
      {slot.title && (
        <p className="absolute bottom-6 left-6 right-20 font-body text-base md:text-lg font-semibold text-white leading-tight">
          {slot.title}
        </p>
      )}

      {/* Circular + button (bottom-right) */}
      <span
        aria-hidden="true"
        className="absolute bottom-6 right-6 w-10 h-10 flex items-center justify-center rounded-full border border-white/70 text-white bg-transparent group-hover:bg-white group-hover:text-black transition-all duration-200"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </span>
    </a>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function ResidentAccess() {
  const card1 = useSlotGroup('residentaccess.card_1', card1Def);
  const card2 = useSlotGroup('residentaccess.card_2', card2Def);
  const card3 = useSlotGroup('residentaccess.card_3', card3Def);

  return (
    <section className="bg-white pb-16 md:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          <EditableSlotGroup
            groupPath="residentaccess.card_1"
            definition={card1Def}
            className="block"
          >
            <ResourceCard slot={card1} fallbackUrl="#" />
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="residentaccess.card_2"
            definition={card2Def}
            className="block"
          >
            <ResourceCard slot={card2} fallbackUrl="#" />
          </EditableSlotGroup>

          <EditableSlotGroup
            groupPath="residentaccess.card_3"
            definition={card3Def}
            className="block"
          >
            <ResourceCard slot={card3} fallbackUrl="/contact-us" />
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'ResidentAccess',
  displayName: 'Resident Access',
  tokens: {},
  slotGroups: {
    'residentaccess.card_1': card1Def,
    'residentaccess.card_2': card2Def,
    'residentaccess.card_3': card3Def,
  },
};

(ResidentAccess as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ResidentAccess;
